import { TABLE, TD, TH } from "@/components/tool-kit/styles";

export interface GridRow {
  label: string;
  /** One cell per column; the same length in every row. */
  cells: readonly string[];
  /** Draw the row heavier — the answer row. */
  strong?: boolean;
}

/**
 * Bits laid out one per column, the way a worksheet lines them up. A real table, so a
 * screen reader announces each row's label and each column's header.
 */
export function BitGrid({
  caption,
  corner,
  head,
  rows,
  faint = 0,
}: {
  /** Names the table for assistive technology; not drawn. */
  caption: string;
  /** Names the label column for assistive technology; not drawn. */
  corner: string;
  /** Optional column headers (bit positions). */
  head?: readonly string[];
  rows: readonly GridRow[];
  /** Leading columns drawn faint — padding zeros. */
  faint?: number;
}) {
  const columns = rows[0]?.cells.length ?? 0;
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={`${TABLE} font-mono`}>
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            <th className={TH} scope="col">
              <span className="sr-only">{corner}</span>
            </th>
            {head
              ? head.map((cell, index) => (
                  <th
                    className={`${TH} px-1 text-center normal-case`}
                    // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional
                    key={index}
                    scope="col"
                  >
                    {cell}
                  </th>
                ))
              : Array.from({ length: columns }, (_, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional
                  <td className={TH} key={index} />
                ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th
                className={`${TD} whitespace-nowrap pr-3 text-left font-medium font-sans text-muted-foreground text-xs`}
                scope="row"
              >
                {row.label}
              </th>
              {row.cells.map((cell, index) => (
                <td
                  className={`${TD} px-1 text-center ${
                    row.strong ? "font-semibold" : ""
                  } ${index < faint ? "text-muted-foreground/40" : ""}`}
                  // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional
                  key={index}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
